import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { message } from 'antd';

const getErrorMessageFromResponseBody = (bodyText) => {
  if (!bodyText) return 'Failed to reset password';
  try {
    const parsed = JSON.parse(bodyText);
    if (typeof parsed === 'string') return parsed;
    if (parsed && typeof parsed === 'object') {
      const fullMessage = [parsed.error, parsed.message, parsed.details]
        .filter(Boolean)
        .join(' ')
        .trim();
      return fullMessage || JSON.stringify(parsed);
    }
    return String(parsed);
  } catch {
    return bodyText;
  }
};

const ResetPassword = () => {
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    const emailFromQuery = searchParams.get('email');
    const codeFromQuery = searchParams.get('code');

    if (emailFromQuery) setEmail(emailFromQuery);
    if (codeFromQuery) setCode(codeFromQuery);
  }, [searchParams]);

  const onFinish = async () => {
    if (!email.trim() || !code.trim()) {
      setError('Email and reset code are required.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match!');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ 
          email: email.trim().toLowerCase(),
          code: code.trim(),
          password
        }),
      });

      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(getErrorMessageFromResponseBody(errorBody));
      }
      
      message.success('Password updated successfully!');
      
      // Small delay to show the success message before redirecting
      setTimeout(() => {
        navigate('/login', { 
          state: { success: 'Password reset successfully! Please log in with your new password.' }
        });
      }, 1500);
    } catch (error) {
      setError(error.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex justify-center items-center h-screen">
      <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
        <div>
          <h2 className="text-2xl font-bold text-center">Reset your password</h2>
          <p className="mt-2 text-sm text-gray-600 text-center">
            Enter your email, reset code, and new password.
          </p>
        </div>
        
        {error && <p className="text-red-500 text-center text-sm">{error}</p>}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onFinish();
          }}
          className="space-y-4"
        >
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="Enter your email address"
            />
          </div>

          <div>
            <label htmlFor="code" className="block text-sm font-medium text-gray-700">
              Reset Code
            </label>
            <input
              type="text"
              id="code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="Enter the code from your email"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              New Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="Enter your new password"
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
              Confirm New Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
              placeholder="Confirm your new password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-white py-2 rounded-lg hover:bg-primary-dark transition duration-300 font-bold disabled:opacity-50"
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <div className="text-center">
          <Link
            to="/login"
            className="font-medium text-primary hover:text-primary-dark"
          >
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
